<template>
  <base-select
    :value="selectedId"
    :options="options"
    valueAttribute="id"
    textAttribute="marketName"
    class="w-64"
    variant="botSelector"
    useOptionSlot
    useLabelSlot
    @input="handleInput"
  >
    <template #label="{ option }">
      <div class="flex items-center gap-1 h-full truncate">
        <answer-published-status v-if="displayPublishedStatus"
          :published="labelItem(option).published"
          :is-unknown-answer="isUnknownAnswer"
          tooltip-position="bottom"
        />

        <vx-tooltip
          position="bottom"
          :title="getBotMarketState(labelItem(option)).tooltip.title"
          :text="getBotMarketState(labelItem(option)).tooltip.text"
        >
          <div class="flex justify-center items-center w-8">
            <SourceAnswerIcon v-if="getBotMarketState(labelItem(option)).type === BOT_MARKET_STATE_TYPE.SOURCE" class="w-4 text-focus"/>
            <vs-icon v-else-if="getBotMarketState(labelItem(option)).type === BOT_MARKET_STATE_TYPE.AUTO_TRANSLATE_ON" icon="sync" class="w-full text-focus"/>
            <vs-icon v-else icon="sync_disabled" class="w-full text-gray"/>
          </div>
        </vx-tooltip>

        <span class="text-gray-dark whitespace-nowrap truncate">
          {{ labelText(option) }}
        </span>
      </div>
    </template>

    <template #option="{ option }">
      <div class="flex items-center gap-1 px-2 py-2 rounded cursor-pointer hover:bg-primary-light">
        <answer-published-status v-if="displayPublishedStatus"
          :published="option.raw.published"
          :is-unknown-answer="isUnknownAnswer"
          tooltip-position="bottom"
        />

        <vx-tooltip
          position="bottom"
          :title="getBotMarketState(option.raw).tooltip.title"
          :text="getBotMarketState(option.raw).tooltip.text"
        >
          <div class="flex justify-center items-center w-8">
            <SourceAnswerIcon v-if="getBotMarketState(option.raw).type === BOT_MARKET_STATE_TYPE.SOURCE" class="w-4 text-focus"/>
            <vs-icon v-else-if="getBotMarketState(option.raw).type === BOT_MARKET_STATE_TYPE.AUTO_TRANSLATE_ON" icon="sync" class="w-full text-focus"/>
            <vs-icon v-else icon="sync_disabled" class="w-full text-gray"/>
          </div>
        </vx-tooltip>

        <span class="text-gray-dark whitespace-nowrap truncate">
          {{ option.text }}
        </span>
      </div>
    </template>
  </base-select>
</template>

<script>
import BaseSelect from '@/components/base-components/BaseSelect.vue'
import AnswerPublishedStatus from '@/components/answers/AnswerPublishedStatus.vue'
import SourceAnswerIcon from '@/assets/images/icons/SourceAnswerIcon.vue'
import botModule from '@/store/modules/bot.module'

const BOT_MARKET_STATE_TYPE = {
  SOURCE: 'source',
  AUTO_TRANSLATE_ON: 'auto-translate-on',
  AUTO_TRANSLATE_OFF: 'auto-translate-off'
}

export default {
  name: 'MultiBotItemSelector',

  components: { BaseSelect, AnswerPublishedStatus, SourceAnswerIcon },

  props: {
    items: {
      type: Array,
      required: true
    },
    selectedId: {
      type: String,
      required: true
    },
    displayPublishedStatus: {
      type: Boolean,
      default: false
    },
    isUnknownAnswer: {
      type: Boolean,
      default: false
    },
    editorType: { // Answer, Event, Block
      type: String,
      default: 'Answer'
    }
  },

  data() {
    return {
      BOT_MARKET_STATE_TYPE
    }
  },

  computed: {
    botVariantList: botModule.getBotVariantList,

    options() {
      return this.items.map(item => ({
        ...item,
        marketName: this.botVariantList.find(botVariant => botVariant.primaryBotId === item.botId)?.name
      }))
    },

    selectedOption() {
      return this.options.find(option => option.id === this.selectedId) || null
    }
  },

  methods: {
    handleInput(value) {
      const selectedItem = this.items.find(item => item.id === value)
      if (!selectedItem) return

      this.$emit('item-selected', value, selectedItem.botId)
    },

    labelItem(option) {
      return this.selectedOption || option?.raw
    },

    labelText(option) {
      return this.selectedOption?.marketName || option?.text || ''
    },

    getBotMarketState(item) {
      const inheritsConversation = item.inheritsConversation
      if (item.multiBotItemId === item.id && !inheritsConversation) {
        return {
          type: BOT_MARKET_STATE_TYPE.SOURCE,
          tooltip: {
            title: `Source ${this.editorType}`,
            text: `${this.editorType} is used as the main version for the chatbot. Other markets are translated from it.`
          }
        }
      }

      if (inheritsConversation || this.editorType === 'Block') {
        return {
          type: BOT_MARKET_STATE_TYPE.AUTO_TRANSLATE_ON,
          tooltip: {
            title: 'Auto-translate: On',
            text: `Changes to the source ${this.editorType.toLowerCase()} will be automatically translated and applied to this market.`
          }
        }
      }

      return {
        type: BOT_MARKET_STATE_TYPE.AUTO_TRANSLATE_OFF,
        tooltip: {
          title: 'Auto-translate: Off',
          text: `Changes to the source ${this.editorType.toLowerCase()} will not be automatically translated or applied to this market.`
        }
      }
    }
  }
}
</script>
